/**
 * Página de éxito post-compra (rutas: `/muchas-gracias`, `/invitacion-premium/success`).
 *
 * Valida el pago vía `/api/validate-payment` (Stripe: `session_id` / `payment_intent`; metadata `plan_tier`).
 * Según el plan resuelto (**basic** | **standard** | **vip**): textos, estilos y enlace final distintos.
 *
 * **VIP:** botón principal y segundo botón opcionales — URLs configuradas desde /admin/memberships.
 * Basic/standard: dashboard → `telegram_url` en query param (pasado por el checkout).
 */

import { useSEO } from "@/hooks/useSEO";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { AlertCircle, CheckCircle, Clock, Crown, ExternalLink, Loader2, Mail, UserPlus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";
import { BRAND_PERSON_NAME } from "@/lib/brandConfig";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import { trackPurchaseDual } from "@/lib/metaCapi";
import { trackDataFastGoal } from "@/lib/datafast";

const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif';

function isSafeCtaUrl(url: string): boolean {
  if (url.startsWith("/")) return true;
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}

/** Stripe vs Mercado Pago: `provider` en query o heurística por params típicos de MP. */
function resolvePaymentProvider(sp: URLSearchParams): "mercadopago" | "stripe" {
  const raw = sp.get("provider")?.toLowerCase().trim();
  if (raw === "mercadopago" || raw === "mp") return "mercadopago";
  if (
    sp.get("collection_id") ||
    sp.get("collection_status") ||
    sp.get("preference_id") ||
    sp.get("preapproval_id") ||
    sp.get("payment_id")
  ) {
    return "mercadopago";
  }
  return "stripe";
}

// ─── Labels por idioma ────────────────────────────────────────────────────────

interface SuccessLabels {
  // Plan basic — acceso directo al grupo privado de Telegram
  basicTitle: string;
  basicSubtitle: string;
  basicMessage: string;
  basicCta: string;
  // Plan standard — canal Telegram estándar
  title: string;
  subtitle: string;
  processedBy: string;
  standardMessage: string;
  standardCta: string;
  // Plan VIP — portal exclusivo
  vipThanksTitle: string;
  vipThanksSubtitle: string;
  vipMessage: string;
  vipCta: string;
  /** Segundo CTA VIP en /muchas-gracias (URL distinta). */
  vipSecondaryCta: string;
  vipFooterHint: string;
  // Estados
  validating: string;
  errorTitle: string;
  errorHelp: string;
  pendingTitle: string;
  pendingMessage: string;
  pendingStep1: string;
  pendingStep2: string;
  pendingStep3: string;
  pendingEmailHint: string;
  pendingAutoUpdate: string;
}

const LABELS: Record<string, SuccessLabels> = {
  es: {
    basicTitle: "¡Acceso activado!",
    basicSubtitle: "Tu membresía Basic está lista",
    basicMessage: "Ya podés ingresar al grupo privado de Telegram. Hacé clic en el botón para unirte ahora.",
    basicCta: "Ingresar al Grupo Privado",
    title: "¡Pago exitoso!",
    subtitle: "Tu acceso está listo",
    processedBy: "Procesado por",
    standardMessage: `Bienvenido al canal exclusivo de ${BRAND_PERSON_NAME}. Hacé clic para unirte al grupo de Telegram.`,
    standardCta: "Ingresar al canal",
    vipThanksTitle: "¡Muchas gracias!",
    vipThanksSubtitle: "Tu membresía VIP ya está activa.",
    vipMessage:
      "Para continuar y acceder a todo el contenido exclusivo, ingresá al portal exclusivo y completá tu registro.",
    vipCta: "Ir al portal exclusivo",
    vipSecondaryCta: "Otro enlace",
    vipFooterHint: "Se abrirá el registro del portal en una nueva pestaña.",
    validating: "Verificando tu pago...",
    errorTitle: "No pudimos verificar tu pago",
    errorHelp: "Si realizaste el pago correctamente, contactá al soporte con tu número de transacción.",
    pendingTitle: "Recibimos tu pago",
    pendingMessage: "Tu membresía está en revisión. El equipo la aprobará pronto.",
    pendingStep1: "Pago recibido",
    pendingStep2: "Revisando tu membresía",
    pendingStep3: "Te enviamos tu acceso por email",
    pendingEmailHint: "Cuando aprobemos tu acceso, te llegará un email con un enlace para ingresar. Revisá tu bandeja de entrada (y la carpeta de spam).",
    pendingAutoUpdate: "Esta página se actualiza automáticamente",
  },
  en: {
    basicTitle: "Access activated!",
    basicSubtitle: "Your Basic membership is ready",
    basicMessage: "You can now join the private Telegram group. Click the button below to enter.",
    basicCta: "Join Private Group",
    title: "Payment successful!",
    subtitle: "Your access is ready",
    processedBy: "Processed by",
    standardMessage: `Welcome to ${BRAND_PERSON_NAME}'s exclusive channel. Click to join the Telegram group.`,
    standardCta: "Join the channel",
    vipThanksTitle: "Thank you so much!",
    vipThanksSubtitle: "Your VIP membership is now active.",
    vipMessage:
      "To continue and access all exclusive content, go to the exclusive portal and complete your registration.",
    vipCta: "Go to exclusive portal",
    vipSecondaryCta: "Another link",
    vipFooterHint: "Registration will open in a new tab.",
    validating: "Verifying your payment...",
    errorTitle: "We could not verify your payment",
    errorHelp: "If you completed the payment, please contact support with your transaction number.",
    pendingTitle: "Payment received",
    pendingMessage: "Your membership is under review. Our team will approve it shortly.",
    pendingStep1: "Payment received",
    pendingStep2: "Reviewing your membership",
    pendingStep3: "We'll email you your access link",
    pendingEmailHint: "Once your access is approved, you'll receive an email with a link to get in. Check your inbox (and spam folder).",
    pendingAutoUpdate: "This page updates automatically",
  },
  pt: {
    basicTitle: "Acesso ativado!",
    basicSubtitle: "Sua assinatura Basic está pronta",
    basicMessage: "Agora você pode entrar no grupo privado do Telegram. Clique no botão abaixo para ingressar.",
    basicCta: "Entrar no Grupo Privado",
    title: "Pagamento aprovado!",
    subtitle: "Seu acesso está pronto",
    processedBy: "Processado por",
    standardMessage: `Bem-vindo ao canal exclusivo da ${BRAND_PERSON_NAME}. Clique para entrar no grupo do Telegram.`,
    standardCta: "Entrar no canal",
    vipThanksTitle: "Muito obrigado!",
    vipThanksSubtitle: "Sua assinatura VIP já está ativa.",
    vipMessage:
      "Para continuar e acessar todo o conteúdo exclusivo, entre no portal exclusivo e complete seu cadastro.",
    vipCta: "Ir ao portal exclusivo",
    vipSecondaryCta: "Outro link",
    vipFooterHint: "O cadastro abrirá em uma nova aba.",
    validating: "Verificando seu pagamento...",
    errorTitle: "Não conseguimos verificar seu pagamento",
    errorHelp: "Se você concluiu o pagamento, entre em contato com o suporte com seu número de transação.",
    pendingTitle: "Pagamento recebido",
    pendingMessage: "Sua assinatura está em revisão. Nossa equipe vai aprová-la em breve.",
    pendingStep1: "Pagamento recebido",
    pendingStep2: "Revisando sua assinatura",
    pendingStep3: "Enviaremos seu acesso por email",
    pendingEmailHint: "Quando aprovarmos seu acesso, você receberá um email com o link para entrar. Verifique sua caixa de entrada (e a pasta de spam).",
    pendingAutoUpdate: "Esta página se atualiza automaticamente",
  },
  de: {
    basicTitle: "Zugang aktiviert!",
    basicSubtitle: "Deine Basic-Mitgliedschaft ist bereit",
    basicMessage: "Du kannst jetzt der privaten Telegram-Gruppe beitreten. Klick auf den Button, um beizutreten.",
    basicCta: "Private Gruppe beitreten",
    title: "Zahlung erfolgreich!",
    subtitle: "Dein Zugang ist bereit",
    processedBy: "Verarbeitet von",
    standardMessage: `Willkommen im exklusiven Kanal von ${BRAND_PERSON_NAME}. Klick, um der Telegram-Gruppe beizutreten.`,
    standardCta: "Kanal beitreten",
    vipThanksTitle: "Vielen Dank!",
    vipThanksSubtitle: "Deine VIP-Mitgliedschaft ist jetzt aktiv.",
    vipMessage:
      "Um fortzufahren und alle exklusiven Inhalte zu sehen, gehe ins exklusive Portal und schließe deine Registrierung ab.",
    vipCta: "Zum exklusiven Portal",
    vipSecondaryCta: "Weiterer Link",
    vipFooterHint: "Die Registrierung öffnet sich in einem neuen Tab.",
    validating: "Zahlung wird überprüft...",
    errorTitle: "Zahlung konnte nicht verifiziert werden",
    errorHelp: "Wenn du die Zahlung abgeschlossen hast, kontaktiere den Support mit deiner Transaktionsnummer.",
    pendingTitle: "Zahlung eingegangen",
    pendingMessage: "Deine Mitgliedschaft wird geprüft. Unser Team genehmigt sie in Kürze.",
    pendingStep1: "Zahlung eingegangen",
    pendingStep2: "Mitgliedschaft wird geprüft",
    pendingStep3: "Wir senden dir deinen Zugang per E-Mail",
    pendingEmailHint: "Sobald dein Zugang freigegeben ist, erhältst du eine E-Mail mit dem Zugangslink. Prüfe dein Postfach (und den Spam-Ordner).",
    pendingAutoUpdate: "Diese Seite aktualisiert sich automatisch",
  },
  fr: {
    basicTitle: "Accès activé !",
    basicSubtitle: "Votre abonnement Basic est prêt",
    basicMessage: "Vous pouvez maintenant rejoindre le groupe privé Telegram. Cliquez sur le bouton pour y accéder.",
    basicCta: "Rejoindre le Groupe Privé",
    title: "Paiement réussi !",
    subtitle: "Votre accès est prêt",
    processedBy: "Traité par",
    standardMessage: `Bienvenue sur la chaîne exclusive de ${BRAND_PERSON_NAME}. Cliquez pour rejoindre le groupe Telegram.`,
    standardCta: "Rejoindre la chaîne",
    vipThanksTitle: "Merci beaucoup !",
    vipThanksSubtitle: "Votre abonnement VIP est maintenant actif.",
    vipMessage:
      "Pour continuer et accéder à tout le contenu exclusif, rendez-vous sur le portail exclusif et finalisez votre inscription.",
    vipCta: "Aller au portail exclusif",
    vipSecondaryCta: "Autre lien",
    vipFooterHint: "L'inscription s'ouvrira dans un nouvel onglet.",
    validating: "Vérification du paiement...",
    errorTitle: "Nous n'avons pas pu vérifier votre paiement",
    errorHelp: "Si vous avez effectué le paiement, contactez le support avec votre numéro de transaction.",
    pendingTitle: "Paiement reçu",
    pendingMessage: "Votre abonnement est en cours d'examen. Notre équipe l'approuvera sous peu.",
    pendingStep1: "Paiement reçu",
    pendingStep2: "Examen de votre abonnement",
    pendingStep3: "Nous vous enverrons votre accès par email",
    pendingEmailHint: "Dès que votre accès sera approuvé, vous recevrez un email avec un lien pour vous connecter. Vérifiez votre boîte de réception (et les spams).",
    pendingAutoUpdate: "Cette page se met à jour automatiquement",
  },
  it: {
    basicTitle: "Accesso attivato!",
    basicSubtitle: "Il tuo abbonamento Basic è pronto",
    basicMessage: "Ora puoi entrare nel gruppo privato Telegram. Clicca sul pulsante per accedere.",
    basicCta: "Entra nel Gruppo Privato",
    title: "Pagamento riuscito!",
    subtitle: "Il tuo accesso è pronto",
    processedBy: "Elaborato da",
    standardMessage: `Benvenuto nel canale esclusivo di ${BRAND_PERSON_NAME}. Clicca per unirti al gruppo Telegram.`,
    standardCta: "Entra nel canale",
    vipThanksTitle: "Grazie mille!",
    vipThanksSubtitle: "Il tuo abbonamento VIP è ora attivo.",
    vipMessage:
      "Per continuare e accedere a tutti i contenuti esclusivi, entra nel portale esclusivo e completa la registrazione.",
    vipCta: "Vai al portale esclusivo",
    vipSecondaryCta: "Altro link",
    vipFooterHint: "La registrazione si aprirà in una nuova scheda.",
    validating: "Verifica del pagamento in corso...",
    errorTitle: "Non siamo riusciti a verificare il tuo pagamento",
    errorHelp: "Se hai completato il pagamento, contatta il supporto con il tuo numero di transazione.",
    pendingTitle: "Pagamento ricevuto",
    pendingMessage: "Il tuo abbonamento è in fase di revisione. Il team lo approverà a breve.",
    pendingStep1: "Pagamento ricevuto",
    pendingStep2: "Revisione dell'abbonamento",
    pendingStep3: "Ti invieremo l'accesso per email",
    pendingEmailHint: "Quando il tuo accesso sarà approvato, riceverai un'email con il link per accedere. Controlla la tua casella di posta (e la cartella spam).",
    pendingAutoUpdate: "Questa pagina si aggiorna automaticamente",
  },
};

function getLabels(language: string): SuccessLabels {
  const base = language.split("-")[0].toLowerCase();
  return LABELS[base] ?? LABELS["en"];
}

type ValidationState = "loading" | "valid" | "pending" | "error";

// ─── Componente ───────────────────────────────────────────────────────────────

export default function PremiumInviteSuccess() {
  useSEO();
  const [searchParams] = useSearchParams();
  const { language: workerLanguage } = useTemplateSelection();
  const capturePremiumEvent = (event: string, props?: Record<string, unknown>) => {
    if (typeof window === "undefined") return;
    const posthog = (window as unknown as { posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void } }).posthog;
    if (posthog && typeof posthog.capture === "function") {
      posthog.capture(event, props);
    }
  };

  const language        = searchParams.get("lang") || workerLanguage || "es";
  const provider        = resolvePaymentProvider(searchParams);
  const gatewayLabel    = provider === "mercadopago" ? "Mercado Pago" : "Stripe";
  const paymentIntentId = searchParams.get("payment_intent");
  const sessionId       = searchParams.get("session_id") || searchParams.get("checkout_session_id");
  const planFromUrl     = searchParams.get("plan");
  const flowTraceId     = searchParams.get("flow_trace_id") || undefined;
  /** URL del grupo de Telegram pasada por BioTemplatePremiumSimple al iniciar el checkout. */
  const telegramUrlFromParam = searchParams.get("telegram_url") || undefined;
  // MercadoPago params — appended automatically to back_url after payment
  const preapprovalId = searchParams.get("preapproval_id");
  const mpPaymentId   = searchParams.get("collection_id") || searchParams.get("payment_id");
  // Magic link token — sent by email when admin manually confirms a pending MP payment
  const magicToken    = searchParams.get("magic_token");

  const L = getLabels(language);

  const [validationState, setValidationState] = useState<ValidationState>("loading");
  const [validatedPlan, setValidatedPlan]     = useState<"basic" | "standard" | "vip">("basic");
  const [redirectUrl, setRedirectUrl]         = useState<string | null>(null);
  const [redirectLabel, setRedirectLabel]     = useState<string | null>(null);
  const [secondaryRedirectUrl, setSecondaryRedirectUrl]   = useState<string | null>(null);
  const [secondaryRedirectLabel, setSecondaryRedirectLabel] = useState<string | null>(null);
  const [errorMessage, setErrorMessage]       = useState<string | null>(null);
  // Guard: la success page se monta dos veces en StrictMode (dev) y a veces
  // los useEffects re-corren ante cambios de auth state. Evitamos doble fire
  // del Purchase para no inflar conversiones en Meta.
  const purchaseFiredRef = useRef(false);
  const firePurchasePixel = (
    plan: "basic" | "standard" | "vip",
    amountCents?: number,
    currency?: string,
  ) => {
    if (purchaseFiredRef.current) return;
    purchaseFiredRef.current = true;
    trackPurchaseDual({
      value: typeof amountCents === "number" ? amountCents / 100 : 0,
      currency: currency || "USD",
      contentName: `Premium invite · ${plan}`,
      contentIds: [plan],
      contentType: "product",
      orderId: paymentIntentId || sessionId || mpPaymentId || preapprovalId || undefined,
    });
    // Funnel goal only — revenue uses Stripe metadata attribution (not goal name "payment").
    trackDataFastGoal("thank_you_viewed", {
      plan_tier: plan,
      currency: currency || "USD",
      value: typeof amountCents === "number" ? String(amountCents / 100) : "0",
    });
  };

  // ── Clerk auth state — used to route the buyer to the right next step
  // after a validated payment: /registro (sign up) → /completar-registro → /portal.
  // The MP / Stripe webhook later assigns the membership by email; here we
  // only nudge the buyer into the funnel so they land in /portal once approved.
  const { isLoaded: clerkLoaded, isSignedIn } = useAuth();
  const { user: clerkUser } = useUser();
  const clerkEmail =
    clerkUser?.primaryEmailAddress?.emailAddress ||
    clerkUser?.emailAddresses?.[0]?.emailAddress ||
    null;

  // ── Magic token (one-time link from admin email) ─────────────────────────
  useEffect(() => {
    if (!magicToken) return;
    const consume = async () => {
      try {
        const res = await fetch(`/api/magic-link?token=${encodeURIComponent(magicToken)}`);
        const data = await res.json() as { valid: boolean; plan?: string; redirect_url?: string; error?: string };
        if (data.valid) {
          const resolvedPlan: "basic" | "standard" | "vip" =
            data.plan === "vip" ? "vip" : data.plan === "basic" ? "basic" :
            data.plan === "free" ? "basic" : "standard";
          setValidatedPlan(resolvedPlan);
          // If the magic link carries a specific access URL, use it directly
          if (data.redirect_url) setRedirectUrl(data.redirect_url);
          setValidationState("valid");
          // Meta Pixel Purchase — magic-link path no tiene amount/currency.
          firePurchasePixel(resolvedPlan);
        } else {
          setErrorMessage(data.error || "El link de acceso no es válido o ya fue utilizado.");
          setValidationState("error");
        }
      } catch {
        setErrorMessage("Error de conexión al validar el link de acceso.");
        setValidationState("error");
      }
    };
    void consume();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [magicToken]);

  useEffect(() => {
    // Magic token is handled by its own effect above
    if (magicToken) return;

    // ── MercadoPago validation ────────────────────────────────────────────────
    if (provider === "mercadopago") {
      const mpIdentifier = preapprovalId || mpPaymentId;
      if (!mpIdentifier) {
        capturePremiumEvent("premium_success_missing_identifier", {
          provider, language, flow_trace_id: flowTraceId,
          has_preapproval_id: false, has_payment_id: false,
        });
        setErrorMessage("No se encontró el identificador de pago en la URL.");
        setValidationState("error");
        return;
      }
      const validate = async () => {
        try {
          capturePremiumEvent("premium_success_validation_started", {
            provider, language, flow_trace_id: flowTraceId,
            has_preapproval_id: Boolean(preapprovalId),
            has_payment_id: Boolean(mpPaymentId),
          });
          const mpQuery = preapprovalId
            ? `subscription_id=${encodeURIComponent(preapprovalId)}`
            : `payment_id=${encodeURIComponent(mpPaymentId!)}`;
          const traceParam = flowTraceId ? `&flow_trace_id=${encodeURIComponent(flowTraceId)}` : "";
          const res = await fetch(`/api/mercadopago/validate-payment?${mpQuery}${traceParam}`);
          const data = await res.json() as { valid: boolean; plan?: string; pending?: boolean; status?: string; amount?: number; currency?: string; error?: string };
          if (!res.ok) {
            capturePremiumEvent("premium_success_validation_failed", {
              provider, language, flow_trace_id: flowTraceId,
              status: res.status, error: data.error || "invalid_payment",
            });
            setErrorMessage(data.error || "El pago no pudo ser verificado.");
            setValidationState("error");
            return;
          }
          if (data.valid) {
            const resolvedPlan: "basic" | "standard" | "vip" =
              data.plan === "vip" || planFromUrl === "vip" ? "vip" : "standard";
            capturePremiumEvent("premium_success_validated", {
              provider, language, flow_trace_id: flowTraceId, plan_tier: resolvedPlan,
            });
            setValidatedPlan(resolvedPlan);
            setValidationState("valid");
            // Meta Pixel Purchase — MP confirmado. amount/currency desde la API
            // si los expone; sino fire sin value pero con order_id real.
            firePurchasePixel(
              resolvedPlan,
              data.amount,
              data.currency || (mpPaymentId || preapprovalId ? "ARS" : undefined),
            );
            return;
          }
          if (data.pending || data.status === "pending" || data.status === "in_process") {
            capturePremiumEvent("premium_success_validation_pending", {
              provider, language, flow_trace_id: flowTraceId, status: data.status || "pending",
            });
            setValidationState("pending");
            return;
          }
          capturePremiumEvent("premium_success_validation_failed", {
            provider, language, flow_trace_id: flowTraceId,
            status: data.status || "unknown", error: data.error || "invalid_payment",
          });
          setErrorMessage(data.error || "El pago no pudo ser verificado.");
          setValidationState("error");
        } catch {
          capturePremiumEvent("premium_success_validation_failed", {
            provider, language, flow_trace_id: flowTraceId, error: "network_error",
          });
          setErrorMessage("Error de conexión al verificar el pago.");
          setValidationState("error");
        }
      };
      void validate();
      return;
    }

    // ── Stripe validation ─────────────────────────────────────────────────────
    let effectiveSessionId: string | null = sessionId;
    if (!paymentIntentId && !effectiveSessionId) {
      try {
        const raw = localStorage.getItem("premium_last_stripe_session");
        if (raw) {
          const parsed = JSON.parse(raw) as {
            sessionId?: string;
            flowTraceId?: string;
            createdAt?: number;
          };
          const candidate = typeof parsed.sessionId === "string" ? parsed.sessionId.trim() : "";
          const ageMs =
            typeof parsed.createdAt === "number" ? Date.now() - parsed.createdAt : Number.POSITIVE_INFINITY;
          const traceMatches =
            !flowTraceId ||
            !parsed.flowTraceId ||
            String(parsed.flowTraceId).trim() === String(flowTraceId).trim();
          const freshEnough = ageMs < 2 * 60 * 60 * 1000;
          if (candidate && freshEnough && traceMatches) {
            effectiveSessionId = candidate;
          }
        }
      } catch {
        // ignore fallback parse errors
      }
    }

    if (!paymentIntentId && !effectiveSessionId) {
      // Sin identificador de pago en URL → no podemos validar → error
      capturePremiumEvent("premium_success_missing_identifier", {
        provider,
        language,
        flow_trace_id: flowTraceId,
        has_payment_intent: false,
        has_session_id: false,
      });
      setErrorMessage("No se encontró el identificador del pago en la URL (payment_intent/session_id).");
      setValidationState("error");
      return;
    }

    const validate = async () => {
      try {
        capturePremiumEvent("premium_success_validation_started", {
          provider,
          language,
          flow_trace_id: flowTraceId,
          has_payment_intent: Boolean(paymentIntentId),
          has_session_id: Boolean(effectiveSessionId),
        });
        const traceParam = flowTraceId ? `&flow_trace_id=${encodeURIComponent(flowTraceId)}` : "";
        const url = paymentIntentId
          ? `/api/validate-payment?payment_intent=${encodeURIComponent(paymentIntentId)}${traceParam}`
          : `/api/validate-payment?session_id=${encodeURIComponent(effectiveSessionId as string)}${traceParam}`;
        const res = await fetch(url);
        const data = await res.json() as { valid: boolean; pending?: boolean; plan?: string; amount?: number; currency?: string; error?: string };

        if (res.ok && !data.valid && data.pending) {
          // VIP pending admin approval — show waiting room
          capturePremiumEvent("premium_success_validation_pending", {
            provider, language, flow_trace_id: flowTraceId,
            plan_tier: data.plan || "vip", source: "stripe",
          });
          setValidatedPlan("vip");
          setValidationState("pending");
          return;
        }

        if (!res.ok || !data.valid) {
          capturePremiumEvent("premium_success_validation_failed", {
            provider,
            language,
            flow_trace_id: flowTraceId,
            status: res.status,
            error: data.error || "invalid_payment",
            payment_intent: paymentIntentId || undefined,
            session_id: effectiveSessionId || undefined,
          });
          setErrorMessage(data.error || "El pago no pudo ser verificado.");
          setValidationState("error");
          return;
        }

        // "basic" del URL param tiene prioridad porque validate-payment puede retornar
        // "standard" como fallback cuando el tier no se reconoce en la DB.
        // La API ahora sí devuelve "basic" si está en los metadata de Stripe, pero
        // el URL param es la fuente más confiable para este flow.
        const resolvedPlan = planFromUrl === "basic"
          ? "basic"
          : (data.plan || planFromUrl || "standard");
        capturePremiumEvent("premium_success_validated", {
          provider,
          language,
          flow_trace_id: flowTraceId,
          plan_tier: resolvedPlan,
          payment_intent: paymentIntentId || undefined,
          session_id: effectiveSessionId || undefined,
        });
        const typedPlan: "basic" | "standard" | "vip" =
          resolvedPlan === "vip"
            ? "vip"
            : resolvedPlan === "standard"
              ? "standard"
              : "basic";
        setValidatedPlan(typedPlan);
        setValidationState("valid");
        // Meta Pixel Purchase con revenue attribution. validate-payment
        // ahora devuelve amount (en centavos) + currency cuando los conoce.
        firePurchasePixel(typedPlan, data.amount, data.currency);
      } catch {
        capturePremiumEvent("premium_success_validation_failed", {
          provider,
          language,
          flow_trace_id: flowTraceId,
          error: "network_error",
          payment_intent: paymentIntentId || undefined,
          session_id: effectiveSessionId || undefined,
        });
        setErrorMessage("Error de conexión al verificar el pago.");
        setValidationState("error");
      }
    };

    validate();
  }, [paymentIntentId, sessionId, preapprovalId, mpPaymentId, planFromUrl, flowTraceId, provider, language]);

  // ── Polling: auto-detect when pending payment is approved ─────────────────
  // Works for both MercadoPago (validate-payment MP) and Stripe (validate-payment Stripe).
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (validationState !== "pending") {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    const check = async () => {
      try {
        let res: Response;
        if (provider === "mercadopago") {
          const mpIdentifier = preapprovalId || mpPaymentId;
          if (!mpIdentifier) return;
          const mpQuery = preapprovalId
            ? `subscription_id=${encodeURIComponent(preapprovalId)}`
            : `payment_id=${encodeURIComponent(mpPaymentId!)}`;
          res = await fetch(`/api/mercadopago/validate-payment?${mpQuery}`);
        } else {
          // Stripe — poll session_id until admin approves (pending → valid)
          const sid = sessionId;
          if (!sid) return;
          res = await fetch(`/api/validate-payment?session_id=${encodeURIComponent(sid)}`);
        }
        const data = await res.json() as { valid: boolean; plan?: string };
        if (res.ok && data.valid) {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
          setValidatedPlan(data.plan === "vip" ? "vip" : "standard");
          setValidationState("valid");
        }
      } catch {
        // network error — keep polling
      }
    };

    pollingRef.current = setInterval(() => { void check(); }, 30_000);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [validationState, provider, preapprovalId, mpPaymentId, sessionId]);

  // ── Fetch redirect URL for the validated plan tier ────────────────────────
  // Called once the payment is validated and we know the plan tier.
  useEffect(() => {
    if (validationState !== "valid") return;
    const fetchRedirectUrl = async () => {
      try {
        const res = await fetch(`/api/membership/success-config?plan_tier=${encodeURIComponent(validatedPlan)}`);
        if (!res.ok) return;
        const data = await res.json() as {
          success_redirect_url?: string | null;
          success_redirect_label?: string | null;
          success_secondary_redirect_url?: string | null;
          success_secondary_redirect_label?: string | null;
        };
        if (data.success_redirect_url) setRedirectUrl(data.success_redirect_url);
        if (data.success_redirect_label) setRedirectLabel(data.success_redirect_label);
        if (data.success_secondary_redirect_url) setSecondaryRedirectUrl(data.success_secondary_redirect_url);
        if (data.success_secondary_redirect_label) setSecondaryRedirectLabel(data.success_secondary_redirect_label);
      } catch {
        // Non-fatal — falls back to env vars / hardcoded defaults below
      }
    };
    void fetchRedirectUrl();
  }, [validationState, validatedPlan]);

  // ── Derivados visuales ────────────────────────────────────────────────────
  const isVip      = validatedPlan === "vip";
  const isBasic    = validatedPlan === "basic";
  // basic → sky/Telegram blue  |  standard → green  |  vip → amber/gold
  const gradient = isVip
    ? "from-amber-400 via-orange-500 to-yellow-500"
    : isBasic
      ? "from-sky-400 via-blue-500 to-cyan-500"
      : "from-green-500 via-emerald-500 to-teal-500";
  const glowColor = isVip
    ? "bg-amber-500/40"
    : isBasic
      ? "bg-sky-500/40"
      : "bg-green-500/40";
  const btnGradient = isVip
    ? "from-amber-500 to-orange-500"
    : isBasic
      ? "from-sky-500 to-blue-600"
      : "from-green-500 to-emerald-500";

  // Redirect URL — single source of truth: admin/memberships config (via /api/membership/success-config).
  // VIP: configured URL only; never falls back to telegram_url.
  // Basic/standard: configured URL → telegram_url param (passed by checkout).
  const primaryCtaUrl = isVip
    ? (redirectUrl ?? null)
    : (redirectUrl ?? telegramUrlFromParam ?? null);

  const secondaryCtaUrl =
    isVip && secondaryRedirectUrl && isSafeCtaUrl(secondaryRedirectUrl) && secondaryRedirectUrl !== primaryCtaUrl
      ? secondaryRedirectUrl
      : null;
  const secondaryCtaLabel = isVip && secondaryCtaUrl
    ? (secondaryRedirectLabel ?? L.vipSecondaryCta)
    : null;

  const pageTitle    = isVip ? L.vipThanksTitle    : isBasic ? L.basicTitle    : L.title;
  const pageSubtitle = isVip ? L.vipThanksSubtitle : isBasic ? L.basicSubtitle : L.subtitle;
  const message      = isVip ? L.vipMessage        : isBasic ? L.basicMessage  : L.standardMessage;
  const ctaLabel     = redirectLabel ?? (isVip ? L.vipCta : isBasic ? L.basicCta : L.standardCta);

  // ── Clerk funnel CTA — where to send the buyer to land in /portal
  // The exact destination depends on Clerk session state:
  //   - signed out          → /registro (sign up; Clerk will collect email)
  //   - signed in           → /portal   (the resolver page already handles
  //                                       "needs profile completion" via
  //                                       its own internal redirect)
  // We carry plan_tier + flow_trace_id forward so the funnel pages can
  // pick up context (e.g. show the right plan in /portal copy) and so the
  // PostHog funnel stitches together end-to-end.
  type FunnelCta = { href: string; label: string; event: string } | null;
  const funnelCta: FunnelCta = (() => {
    if (validationState !== "valid" || !clerkLoaded) return null;
    const planParam = `plan_tier=${encodeURIComponent(validatedPlan)}`;
    const fromParam = "from=muchas-gracias";
    const traceParam = flowTraceId
      ? `&flow_trace_id=${encodeURIComponent(flowTraceId)}`
      : "";
    if (isSignedIn) {
      return {
        href: `${MEMBERSHIP_ROUTE.PORTAL}?${planParam}&${fromParam}${traceParam}`,
        label: isVip ? "Ir al portal exclusivo" : "Ir a mi portal",
        event: "premium_success_clerk_portal_click",
      };
    }
    const emailParam = clerkEmail
      ? `&email=${encodeURIComponent(clerkEmail)}`
      : "";
    return {
      href: `${MEMBERSHIP_ROUTE.REGISTRO}?${planParam}&${fromParam}${emailParam}${traceParam}`,
      label: "Crear cuenta y activar acceso",
      event: "premium_success_clerk_signup_click",
    };
  })();

  // ── Loading ───────────────────────────────────────────────────────────────
  if (validationState === "loading") {
    return (
      <div
        className="min-h-screen bg-black text-white flex items-center justify-center px-4"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="fixed inset-0 -z-10 bg-black" />
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto" />
          <p className="text-gray-400 text-sm">{L.validating}</p>
        </div>
      </div>
    );
  }

  // ── Pendiente (MP: efectivo, QR — en espera de aprobación del admin) ────────
  if (validationState === "pending") {
    return (
      <div
        className="min-h-screen bg-black text-white flex items-center justify-center px-4 py-12"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="fixed inset-0 -z-10 bg-black">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-500 opacity-10" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-black/60 to-black" />
        </div>

        <div className="max-w-sm w-full text-center space-y-8">
          {/* Animated icon */}
          <div className="relative flex justify-center items-center h-32">
            <div className="absolute w-32 h-32 rounded-full bg-amber-500/10 animate-ping" style={{ animationDuration: "2.5s" }} />
            <div className="absolute w-24 h-24 rounded-full bg-amber-500/15 animate-pulse" />
            <div className="relative w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-2xl shadow-amber-500/40">
              <Clock className="w-10 h-10 text-white" />
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-white">{L.pendingTitle}</h1>
            <p className="text-amber-300/70 text-sm">{L.pendingMessage}</p>
          </div>

          {/* Step tracker */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-amber-500/20 text-left space-y-4">
            {/* Step 1 — done */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-500/15 border border-green-500/40 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-green-400" />
              </div>
              <span className="text-green-300 text-sm font-medium">{L.pendingStep1}</span>
            </div>

            <div className="ml-4 h-4 border-l border-dashed border-white/10" />

            {/* Step 2 — current */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 border border-amber-400/50 flex items-center justify-center flex-shrink-0">
                <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-amber-200 text-sm font-semibold">{L.pendingStep2}</span>
              </div>
            </div>

            <div className="ml-4 h-4 border-l border-dashed border-white/10" />

            {/* Step 3 — upcoming */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-gray-500" />
              </div>
              <span className="text-gray-500 text-sm">{L.pendingStep3}</span>
            </div>
          </div>

          {/* Email hint */}
          <div className="bg-amber-500/8 border border-amber-500/20 rounded-xl px-5 py-4">
            <p className="text-amber-200/75 text-xs leading-relaxed">{L.pendingEmailHint}</p>
          </div>

          {/* Auto-update note */}
          <p className="text-gray-600 text-xs flex items-center justify-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block flex-shrink-0" />
            {L.pendingAutoUpdate}
          </p>
        </div>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (validationState === "error") {
    return (
      <div
        className="min-h-screen bg-black text-white flex items-center justify-center px-4"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="fixed inset-0 -z-10 bg-black">
          <div className="absolute inset-0 bg-gradient-to-br from-red-500 via-pink-500 to-rose-500 opacity-15" />
        </div>
        <div className="max-w-md w-full text-center space-y-6">
          <div className="relative inline-flex justify-center w-full">
            <div className="absolute inset-0 blur-2xl bg-red-500/30 animate-pulse" />
            <div className="relative w-24 h-24 bg-gradient-to-br from-red-500 to-rose-500 rounded-full flex items-center justify-center shadow-2xl">
              <AlertCircle className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-red-400">{L.errorTitle}</h1>
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-red-500/20">
            <p className="text-gray-300 text-sm leading-relaxed mb-2">{errorMessage}</p>
            <p className="text-gray-500 text-xs">{L.errorHelp}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Éxito ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-black text-white flex items-center justify-center px-4 select-none"
      style={{ fontFamily: FONT_FAMILY, WebkitUserSelect: "none" } as React.CSSProperties}
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
    >
      <div className="fixed inset-0 -z-10 bg-black">
        <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-20`} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-black/50 to-black" />
      </div>

      <div className="max-w-md w-full text-center space-y-6">
        <div className="relative inline-flex justify-center w-full">
          <div className={`absolute inset-0 blur-2xl ${glowColor} animate-pulse`} />
          <div className={`relative w-24 h-24 bg-gradient-to-br ${gradient} rounded-full flex items-center justify-center shadow-2xl`}>
            {isVip
              ? <Crown className="w-12 h-12 text-white" />
              : <CheckCircle className="w-12 h-12 text-white" />
            }
          </div>
        </div>

        <div>
          <h1 className={`text-3xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>
            {pageTitle}
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            {pageSubtitle}
          </p>
        </div>

        <div
          className={`rounded-2xl p-6 border backdrop-blur-sm select-none ${
            isVip
              ? "border-amber-500/30 bg-amber-500/5"
              : isBasic
                ? "border-sky-500/30 bg-sky-500/5"
                : "border-white/10 bg-white/5"
          }`}
          onContextMenu={(e) => e.preventDefault()}
        >
          <p className="text-gray-300 text-base leading-relaxed mb-4">{message}</p>

          {/* "Procesado por" solo para VIP y standard — basic es más directo */}
          {!isBasic && (
            <p className="text-gray-500 text-xs mb-6">
              {L.processedBy} {gatewayLabel}
            </p>
          )}

          {/* Clerk funnel CTA — drives the buyer to /registro or /portal so
              the membership granted by the webhook is bound to a Clerk user
              and they land in the portal. Sits above the legacy resource CTA
              (Telegram / external portal) which becomes the secondary path. */}
          {funnelCta ? (
            <div className="w-full mb-3">
              <Link
                to={funnelCta.href}
                className={`w-full py-4 px-6 bg-gradient-to-r ${btnGradient} rounded-xl font-bold text-white shadow-lg hover:shadow-2xl transform hover:scale-105 active:scale-95 transition-all duration-200 flex items-center justify-center gap-2 select-none`}
                onContextMenu={(e) => e.preventDefault()}
                onClick={() =>
                  capturePremiumEvent(funnelCta.event, {
                    provider,
                    language,
                    flow_trace_id: flowTraceId,
                    plan_tier: validatedPlan,
                    is_signed_in: isSignedIn,
                  })
                }
              >
                <UserPlus className="w-5 h-5 flex-shrink-0" />
                <span>{funnelCta.label}</span>
              </Link>
            </div>
          ) : null}

          {primaryCtaUrl || secondaryCtaUrl ? (
            <div className="w-full space-y-3">
              {primaryCtaUrl ? (
                <a
                  href={primaryCtaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={
                    funnelCta
                      ? "w-full py-3.5 px-6 rounded-xl font-semibold text-white/90 border border-white/15 bg-white/5 hover:bg-white/10 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 select-none"
                      : `w-full py-4 px-6 bg-gradient-to-r ${btnGradient} rounded-xl font-bold text-white shadow-lg hover:shadow-2xl transform hover:scale-105 active:scale-95 transition-all duration-200 flex items-center justify-center gap-2 select-none`
                  }
                  onContextMenu={(e) => e.preventDefault()}
                  onClick={() =>
                    capturePremiumEvent(
                      isVip
                        ? "premium_success_vip_portal_click"
                        : isBasic
                          ? "premium_success_basic_telegram_click"
                          : "premium_success_telegram_click",
                      {
                        provider,
                        language,
                        flow_trace_id: flowTraceId,
                        plan_tier: validatedPlan,
                        destination_url: primaryCtaUrl,
                        payment_intent: paymentIntentId || undefined,
                        session_id: sessionId || undefined,
                      },
                    )
                  }
                >
                  {isVip ? (
                    <ExternalLink className="w-5 h-5" />
                  ) : (
                    /* Telegram icon for basic and standard plans */
                    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                    </svg>
                  )}
                  <span>{ctaLabel}</span>
                </a>
              ) : null}
              {isVip && secondaryCtaUrl && secondaryCtaLabel ? (
                <a
                  href={secondaryCtaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3.5 px-6 rounded-xl font-semibold text-amber-50 border border-amber-400/45 bg-amber-500/10 hover:bg-amber-500/15 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 select-none"
                  onContextMenu={(e) => e.preventDefault()}
                  onClick={() =>
                    capturePremiumEvent("premium_success_vip_secondary_click", {
                      provider,
                      language,
                      flow_trace_id: flowTraceId,
                      plan_tier: validatedPlan,
                      destination_url: secondaryCtaUrl,
                      payment_intent: paymentIntentId || undefined,
                      session_id: sessionId || undefined,
                    })
                  }
                >
                  <ExternalLink className="w-5 h-5 shrink-0 opacity-90" />
                  <span>{secondaryCtaLabel}</span>
                </a>
              ) : null}
            </div>
          ) : (
            /* No redirect URL configured yet for this brand */
            <div className="w-full py-4 px-6 rounded-xl border border-white/10 text-gray-500 text-sm text-center">
              Tu membresía está activa. El acceso será habilitado en breve — te contactaremos por email.
            </div>
          )}
          {isVip && (primaryCtaUrl || secondaryCtaUrl) && (
            <p className="text-gray-500 text-xs mt-4 text-center">{L.vipFooterHint}</p>
          )}
        </div>
      </div>
    </div>
  );
}
